// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { z } from 'zod';
import { ALL_REQUEST_TYPES } from '../constants/request-types.js';
import { ISO_3166_1_ALPHA_2 } from '../constants/iso-3166-1-alpha-2.js';
/*
 * Sprint 65.15.0 (AC6) — three fields the payload types DECLARE, these schemas
 * dropped, and the service then reached for through `as any`.
 *
 * `PublicIntakePayload` and `AdminCreateCasePayload` (`types/case.ts:251-268`)
 * both declare `customer_ref`, `country` and `preferred_channel`. Neither schema
 * listed them, and zod strips unknown keys — so a caller sending `country: 'US'`
 * got a 201 and a case with `country: null`, silently. `service.ts:345` and
 * `:521` read `(input as any).country ?? null`, and the cast is what kept `tsc`
 * from saying so: the service was asking a type that had never carried the field.
 *
 * The consequence is not cosmetic. `CrossBorderDetector.resolveSubjectCountry`
 * (`cross-border-detector.ts:222`) reads exactly three places for the data
 * subject's country — `metadata.subject_residence_country`, `caseDoc.country`,
 * and `metadata.subject_address.country`. The first and third are written by
 * nothing in the platform (grep: they appear only inside that method), and the
 * second could not be set by any intake path. So the cross-border transfer
 * trigger — a statutory control, and the reason `XBD-` dossiers exist — could
 * only fire against a case someone had edited directly in the database. Measured
 * on the E2E tenant: an overseas subject with a US address returned
 * `triggered: false, reason: 'subject_country_unknown', subject_country: null`.
 *
 * ISO 3166-1 alpha-2, upper-cased, because `resolveOrgCountry` compares it to
 * the org's own code with `===` and 'us' !== 'US' would read as cross-border
 * between a country and itself.
 *
 * ## The message says ISO 3166-1 alpha-2, and now so does the CHECK
 *
 * This was `.length(2)` and nothing else, so `ZZ`, `XX`, `QQ` and `EU` parsed
 * and were stored while the error text asserted a standard none of them belong
 * to. That is not a cosmetic gap: the paragraph above is the reason. A junk
 * two-letter value is not equal to the org's code, so `===` reads it as a real
 * jurisdiction difference and it drives a cross-border determination — a
 * statutory control deciding on a string that means nothing.
 *
 * `ISO_3166_1_ALPHA_2` is the 249 officially assigned codes, and existed for
 * exactly this before it had a backend or validation call site at all (its only
 * consumer was the frontend dropdown). Its own docblock records why CLDR's 280
 * must not be used: it carries `ZZ`, `XA`, `XB`, `EU` and the deprecated
 * aliases. The refinement runs AFTER the upper-casing transform, so `' us '`
 * still parses — the normalisation is what the membership test is applied to.
 */
const isoCountryCode = z
    .string()
    .trim()
    .length(2, 'country must be an ISO 3166-1 alpha-2 code')
    .transform((c) => c.toUpperCase())
    .refine((c) => ISO_3166_1_ALPHA_2.includes(c), {
    message: 'country must be an ISO 3166-1 alpha-2 code',
});
/**
 * `country` as the intake FORMS send it: absent, null, blank, or a code.
 *
 * `.length(2)` admits `null`, `undefined` and a two-character string, and an
 * empty string is none of the three. `IntakeFormPage` — the public rights
 * request form — initialises `country: ''` and posts the form object verbatim,
 * so a data subject who never touched the optional "Country / Jurisdiction"
 * field got `400 validation_error: country must be an ISO 3166-1 alpha-2 code`
 * on the DEFAULT state of the form. Before `country` joined the schema, zod
 * stripped the unknown key and the submission succeeded.
 *
 * A blank field and an absent field are the same statement from a form, and the
 * stored column is `| null`, so blank normalises to `null` HERE rather than
 * each of the two forms being asked to remember `|| null` — which is exactly
 * the difference between the two forms that produced this defect.
 *
 * Whitespace alone is the same untouched field with a stray keystroke; it must
 * not reach the length check and be read as a one-character code.
 */
const optionalIsoCountryCode = z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), isoCountryCode.nullable().optional());
const PREFERRED_CHANNELS = ['email', 'postal', 'phone', 'portal'];
/**
 * How a request reached the operator — the `CaseSource` values a HUMAN observes.
 *
 * Sprint 65.15.0 (AC6) — found by the schema/payload parity guard, which flagged
 * `source` as a fourth declared field the schema stripped. `service.ts:485`
 * hardcodes `admin_manual`, so of the nine `CaseSource` values only two were
 * ever reachable: `public_form` (set by the public route) and `admin_manual`.
 * A DPO who takes a rights request by post, by phone or at a counter — all of
 * which Vietnam PDPL contemplates, and all of which have their own value in the
 * union — had no way to record how it arrived.
 *
 * `public_form`, `cmp_gateway` and `aesirx_cmp` are deliberately NOT here. Those
 * are provenance the SYSTEM asserts about its own ingress, and a caller able to
 * claim them could make an operator-entered case look like it came from the
 * public form or the consent gateway. Provenance a caller can choose is not
 * provenance.
 */
const OPERATOR_RECORDED_SOURCES = [
    'admin_manual',
    'phone_received',
    'postal_received',
    'email_received',
    'in_person',
    'legacy_import',
];
/**
 * Sprint 63.31.0 (OD-E) — the installed right an intake handled in Legal
 * Matters names. Blank reads as not named.
 */
const legalMatterRightCode = z
    .string()
    .trim()
    .max(100)
    .nullable()
    .optional()
    .transform((value) => (value ? value : null));
/**
 * Validation schema for public intake form submission.
 * See: docs/product/phases/phase-1/sprint-1.0.0.md - Epic A
 */
export const publicIntakeSchema = z.object({
    request_type: z.enum(ALL_REQUEST_TYPES),
    requester_name: z.string().min(1, 'Full name is required').max(200),
    requester_email: z.string().email('Valid email is required').max(254),
    requester_phone: z.string().max(20).nullable().optional(),
    /*
     * fix-sprint #447 W9 (owner ruling OR3) — `false` only. Whether a processor
     * or third party must act is the CONTROLLER's fact about its own processing,
     * and it selects the installed record that dates the case: a `third_party`
     * record states a longer period for most request classes. A requester could
     * not know it, and an anonymous submission able to assert it could lengthen
     * the organisation's own deadline. The operator records it on the case page,
     * which re-dates the case from receipt. `false` and absent still parse.
     */
    third_party_involved: z.literal(false).default(false),
    description: z.string().min(1, 'Description is required').max(5000),
    preferred_language: z.string().min(2).max(5).default('en'),
    consent_to_processing: z.literal(true, {
        error: () => ({ message: 'Consent to processing is required' }),
    }),
    customer_ref: z.string().max(200).nullable().optional(),
    country: optionalIsoCountryCode,
    preferred_channel: z.enum(PREFERRED_CHANNELS).nullable().optional(),
    /*
     * Sprint 63.31.0 (owner ruling OD-E) — for a request type handled in Legal
     * Matters, which installed right the requester raises. A string, not an
     * enum: the rights are read from the organisation's installed frameworks
     * (R-46), and the server checks it against them.
     */
    legal_matter_right_code: legalMatterRightCode,
});
/**
 * Validation schema for admin manual case creation.
 * See: docs/product/phases/phase-1/sprint-1.0.0.md - Epic D.2
 */
export const adminCreateCaseSchema = z.object({
    request_type: z.enum(ALL_REQUEST_TYPES),
    requester_name: z.string().min(1).max(200),
    requester_email: z.string().email().max(254),
    requester_phone: z.string().max(20).nullable().optional(),
    requester_id_type: z.string().max(50).nullable().optional(),
    requester_id_value: z.string().max(200).nullable().optional(),
    third_party_involved: z.boolean().default(false),
    business_unit_id: z.string().nullable().optional(),
    description: z.string().max(10000).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    customer_ref: z.string().max(200).nullable().optional(),
    country: optionalIsoCountryCode,
    preferred_channel: z.enum(PREFERRED_CHANNELS).nullable().optional(),
    source: z.enum(OPERATOR_RECORDED_SOURCES).nullable().optional(),
    /* Sprint 63.31.0 (OD-E) — as on the public intake. */
    legal_matter_right_code: legalMatterRightCode,
});
//# sourceMappingURL=intake.js.map